import { Capacitor } from '@capacitor/core';
import { BiometricAuth, BiometryErrorType } from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';

// Only the refresh token is stored — the auth token is always obtained fresh
// via the refresh endpoint on every biometric login, so it never goes stale.
const REFRESH_TOKEN_KEY = 'biometric_refresh_token';
const ENABLED_KEY       = 'biometric_enabled';

export type BiometricStatus =
  | 'available'       // hardware present + biometrics enrolled → ready to use
  | 'not_enrolled'    // hardware present but no fingerprint/face set up on device
  | 'not_available';  // no biometric hardware, or web platform

export async function checkBiometricStatus(): Promise<BiometricStatus> {
  if (!Capacitor.isNativePlatform()) return 'not_available';
  try {
    const info = await BiometricAuth.checkBiometry();
    if (info.isAvailable) return 'available';
    if (info.code === BiometryErrorType.biometryNotEnrolled) return 'not_enrolled';
    return 'not_available';
  } catch {
    return 'not_available';
  }
}

export async function isBiometricAvailable(): Promise<boolean> {
  const status = await checkBiometricStatus();
  // Show the toggle for 'available' AND 'not_enrolled' — in both cases hardware exists.
  // The toggle will be visible so the user knows the feature exists, but enabling it
  // will prompt them to set up biometrics in device settings if not yet enrolled.
  return status !== 'not_available';
}

export function isBiometricEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) === 'true';
}

export async function enableBiometric(refreshToken: string): Promise<void> {
  await Preferences.set({ key: REFRESH_TOKEN_KEY, value: refreshToken });
  localStorage.setItem(ENABLED_KEY, 'true');
}

export async function disableBiometric(): Promise<void> {
  localStorage.removeItem(ENABLED_KEY);
  await Preferences.remove({ key: REFRESH_TOKEN_KEY });
}

export async function biometricVerify(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await BiometricAuth.authenticate({
      reason: 'Log in to RentMy Admin',
      cancelTitle: 'Use Password',
      androidTitle: 'Biometric Login',
      androidSubtitle: 'Use your fingerprint or face to sign in',
      allowDeviceCredential: false,
    });
    return true;
  } catch {
    return false;
  }
}

export async function getBiometricRefreshToken(): Promise<string | null> {
  const { value } = await Preferences.get({ key: REFRESH_TOKEN_KEY });
  return value ?? null;
}

// Called by http.ts (via main.ts hook) whenever the 401 interceptor rotates
// the refresh token, so Preferences stays in sync.
export async function syncBiometricRefreshToken(newRefreshToken: string): Promise<void> {
  if (!isBiometricEnabled()) return;
  await Preferences.set({ key: REFRESH_TOKEN_KEY, value: newRefreshToken });
}
